import { useRoleObject } from '@/data/role';
import { useSelf } from '@/data/user';

/**
 * The current user's access level, and the gates derived from it.
 *
 * The level comes from the role they are assigned, not from the legacy `roles` array —
 * that array is on its way out, and reading the role here means removing it from the API
 * later is a no-op for the ~140 call sites of this hook. Roles are loaded and gated in
 * CompanyAuthentication, so the lookup is always ready.
 *
 * A user with no role_uid falls back to the legacy array, mirroring what `authenticate`
 * does server-side. That fallback goes away with the array itself.
 *
 * Zebelers pass every gate, mirroring the is_zebeler short-circuit in `roleCheck`. They
 * need it: a zebeler viewing another company holds a role_uid from their *own* company,
 * which isn't in that company's role list, so no level resolves for them at all.
 */
export const useUserRoles = () => {
  const self = useSelf(false);
  const role = useRoleObject(self.data.role_uid);
  const level = role?.permissions?.[0] ?? self.data.roles[0];

  return {
    hasAdminAccess: level === 'admin' || self.data.is_zebeler,
    hasEditorAccess: ['admin', 'editor'].includes(level) || self.data.is_zebeler,
    isZebeler: self.data.is_zebeler
  };
};
